import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent } from "react";

import { Footer, Nav } from "../components/lidoo/chrome";
import { MiniClubCard } from "../components/lidoo/club-card";
import { homeSummary, type HomeRegion } from "../lib/api/lidoo.functions";
import { HERO_IMAGE, heroSrc, REGION_CARDS } from "../lib/geo";
import { DICTS, fill, regionName, useI18n } from "../lib/i18n";
import { hreflangLinks } from "../lib/seo";

export const Route = createFileRoute("/")({
  loader: async () => await homeSummary(),
  // The English homepage had no title tag at all: it inherited whatever the
  // root set, which is the one page on the site that most needs its own.
  head: (ctx) => ({
    meta: [
      {
        title: `${fill(DICTS.en.hero_title_1, { n: String(ctx.loaderData?.total ?? "") })} ${
          DICTS.en.hero_title_2
        } | sunbedly`,
      },
      { name: "description", content: DICTS.en.hero_sub },
    ],
    links: [
      ...hreflangLinks("/"),
      {
        rel: "preload",
        as: "image",
        href: heroSrc(960),
        media: "(max-width: 640px)",
        fetchPriority: "high",
      },
      {
        rel: "preload",
        as: "image",
        imageSrcSet: `${heroSrc(1440)} 1440w, ${heroSrc(1920)} 1920w, ${heroSrc(2400)} 2400w`,
        imageSizes: "100vw",
        media: "(min-width: 641px)",
        fetchPriority: "high",
      },
    ],
  }),
  component: () => {
    const { total, regions } = Route.useLoaderData();
    return <Index total={total} regions={regions} />;
  },
});

// A below-the-fold cover that genuinely waits for the visitor. loading="lazy"
// was not enough: Chrome's lazy threshold is 1250 px or more, which put all
// six destination covers (~600 KB) on the wire alongside the hero image — the
// hero is the LCP, and on a throttled phone it took 3.5 s to download 68 KB
// because these were sharing the pipe. No src until the card is within 300 px
// of the viewport, so the first seconds of bandwidth belong to the hero.
// Rendered without src on the server too, which keeps hydration clean; the
// crawler still gets the surrounding link, heading and alt text.
function DeferredCover({
  src,
  srcSet,
  sizes,
  alt,
  width,
  height,
  className,
}: {
  src: string;
  srcSet?: string | null;
  sizes?: string;
  alt: string;
  width?: number | null;
  height?: number | null;
  className: string;
}) {
  const ref = useRef<HTMLImageElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            // A cover that fails to load (a Google photo can) steps aside for
            // the dark tile behind it instead of showing a broken image.
            el.onerror = () => {
              el.style.visibility = "hidden";
            };
            if (srcSet) {
              el.srcset = srcSet;
              if (sizes) el.sizes = sizes;
            }
            el.src = src;
            io.disconnect();
          }
        }
      },
      { rootMargin: "300px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [src, srcSet, sizes]);
  return (
    <img
      ref={ref}
      alt={alt}
      width={width ?? undefined}
      height={height ?? undefined}
      decoding="async"
      className={className}
    />
  );
}

// A destination card is a third of the content width on a desktop, half on a
// tablet, the full width on a phone; the 800 px rendering covers all three at
// 1x and the phone at 2x, the 1600 one the rest.
const CARD_SIZES = "(min-width: 1024px) 384px, (min-width: 640px) 50vw, 100vw";

function SearchCard() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [locating, setLocating] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void navigate({ to: "/clubs", search: { q: q || undefined } });
  };

  const nearMe = () => {
    if (!("geolocation" in navigator)) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        void navigate({
          to: "/clubs",
          search: {
            near: `${pos.coords.latitude.toFixed(4)},${pos.coords.longitude.toFixed(4)}`,
          },
        });
      },
      () => setLocating(false),
      { timeout: 8000 },
    );
  };

  return (
    <form
      onSubmit={submit}
      className="w-full max-w-xl rounded-2xl bg-[#F2EFE9] p-4 shadow-[0_30px_70px_-20px_rgba(10,20,35,0.7)]"
    >
      <label
        htmlFor="hero-search"
        className="li-mono block px-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#5B6472]"
      >
        {t("search_label")}
      </label>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <svg
            className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#5B6472]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <input
            id="hero-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("search_placeholder")}
            className="li-display h-14 w-full rounded-xl border border-[#1C2430]/20 bg-white pl-11 pr-4 text-base text-[#1C2430] placeholder:text-[#5B6472] focus:border-[#2C4FD6] focus:outline-none focus:ring-2 focus:ring-[#2C4FD6]/25"
          />
        </div>
        <button
          type="submit"
          className="li-display group flex h-14 shrink-0 items-center justify-center gap-2 rounded-xl bg-[#1C2430] px-6 text-base font-bold text-[#F2EFE9] transition-colors hover:bg-[#2C4FD6] active:translate-y-[1px]"
        >
          {t("search_cta")}
          <span className="li-mono text-xs">→</span>
        </button>
      </div>
      <button
        type="button"
        onClick={nearMe}
        className="li-mono mt-3 inline-flex items-center gap-2 rounded-full border border-[#1C2430]/25 px-4 py-2 text-[12px] text-[#1C2430] transition-transform hover:border-[#2C4FD6] hover:text-[#2C4FD6] active:scale-[0.96]"
      >
        <span className="inline-block h-2 w-2 rounded-full bg-[#2C4FD6]" />
        {locating ? t("locating") : t("near_me")}
      </button>
    </form>
  );
}

// Takes its data as props: the /nl, /it, /de ... homepages render this same
// component from their own loader, and reading Route.useLoaderData() here would
// read the English route's data no matter which URL is actually being served.
export function Index({ total, regions }: { total: number; regions: HomeRegion[] }) {
  const { t } = useI18n();

  // The destination grid is driven by the data, so a new coast appears the
  // moment it has three clubs. A coast with a stored photograph of its own
  // (region_photos, chosen by hand from Pexels or Wikimedia Commons) leads
  // with that; the six coasts with generated artwork fall back to it; the
  // rest borrow the picture of their strongest club.
  const art = new Map(REGION_CARDS.map((r) => [r.key as string, r.image as string]));
  const realRegions = regions.map((r) => ({
    ...r,
    image: r.photo?.src ?? art.get(r.key) ?? r.cover,
    imageSrcSet: r.photo?.srcSet ?? null,
    imageWidth: r.photo?.width ?? null,
    imageHeight: r.photo?.height ?? null,
    // A stored photo says what it shows; artwork and club covers keep the
    // coast's name, which is all that is known about them here.
    imageAlt: r.photo?.alt ?? null,
  }));
  // Club rows only for the first four coasts. Beyond that a visitor is
  // scrolling past listings for a sea they were never going to.
  const clubsByRegion = regions.slice(0, 4);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": "https://sunbedly.com/#org",
      name: "sunbedly",
      url: "https://sunbedly.com",
      logo: "https://sunbedly.com/favicon-32.png",
      description:
        "sunbedly maps real beach clubs across Europe, coast by coast, with real prices and direct booking requests.",
      foundingDate: "2026-07",
      email: "hello@sunbedly.com",
      // The written-down editorial method: which sources count, which are
      // refused, and the corrections made. The strongest machine-readable
      // "we are not a scraped directory" signal schema.org offers.
      publishingPrinciples: "https://sunbedly.com/how-we-verify",
      // The registered entity behind the brand, matching the colophon on
      // /terms. An anonymous directory and a registered one read completely
      // differently to a spam classifier, which is the point of shipping this.
      // "sunbedly" is itself the registered handelsnaam (KvK 95627960, added
      // 24 aug 2026), so the identifier and address sit directly on the
      // organization rather than on a differently-named parent. City only:
      // no street or postcode, it is a home address (Sam, 30 Sep 2026).
      identifier: {
        "@type": "PropertyValue",
        propertyID: "KVK",
        value: "95627960",
      },
      address: {
        "@type": "PostalAddress",
        addressLocality: "Nijmegen",
        addressCountry: "NL",
      },
      sameAs: ["https://www.instagram.com/sunbedly/"],
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": "https://sunbedly.com/#website",
      name: "sunbedly",
      url: "https://sunbedly.com",
      publisher: { "@id": "https://sunbedly.com/#org" },
      potentialAction: {
        "@type": "SearchAction",
        target: "https://sunbedly.com/clubs?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: "How to book a beach club on sunbedly",
      step: [
        { "@type": "HowToStep", name: "Find your beach", text: "Search by region or share your location. Every club in one list." },
        { "@type": "HowToStep", name: "Compare real prices", text: "See beds, parasols and what is included, no calling around." },
        { "@type": "HowToStep", name: "Book your bed", text: "Send one request and the club confirms directly." },
      ],
    },
  ];

  return (
    <div className="min-h-dvh bg-[#F2EFE9]">
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Nav />

      {/* 1. Hero: the search sits fully inside the image over a heavy scrim.
          The earlier version floated it on the image/cream seam at partial
          opacity, which is what kept reading as washed out — here the card is
          opaque and the plate behind it is dark at every viewport width. */}
      {/* The nav floats as a pill over the photograph, so the hero is pulled up
          behind it and pays that height back as top padding. */}
      <section className="relative isolate -mt-[88px] overflow-hidden md:-mt-[104px]">
        {/* This image IS the Largest Contentful Paint of the whole site.
            Lighthouse measured it at 14.8 s on a phone: 963 KB of JPEG for a
            400 px viewport. The srcset serves a 119 KB WebP there instead, and
            the matching preload in the head starts the download before the
            stylesheet has finished parsing. */}
        <picture>
          {/* A phone gets the 960 file even on a retina screen: the photo
              sits behind a dark scrim, so serving 2.6x density there is paying
              for sharpness nobody can see. */}
          <source media="(max-width: 640px)" srcSet={heroSrc(960)} />
          <source
            srcSet={`${heroSrc(1440)} 1440w, ${heroSrc(1920)} 1920w, ${heroSrc(2400)} 2400w`}
            sizes="100vw"
          />
          <img
            src={heroSrc(1440)}
            alt={HERO_IMAGE.alt}
            width={HERO_IMAGE.width}
            height={HERO_IMAGE.height}
            className="absolute inset-0 -z-10 h-full w-full object-cover"
            loading="eager"
            fetchPriority="high"
          />
        </picture>
        {/* The photograph is deliberately a light one, so the scrim has to do
            more work than a dark image would need — but only where the words
            are. A flat veil carries mobile, where the copy spans the full
            width; on wider screens it falls away to the right and lets the
            water and parasols stay bright. */}
        <div className="absolute inset-0 -z-10 bg-[#0B1420]/55 md:bg-transparent" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#0B1420]/62 via-[#0B1420]/25 to-transparent md:from-[#0B1420]/78 md:via-[#0B1420]/45 md:to-transparent" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#0B1420]/45 to-transparent" />
        <div className="mx-auto max-w-6xl px-5 pb-20 pt-[152px] md:pb-28 md:pt-[184px] lg:pb-36 lg:pt-[220px]">
          <div className="max-w-2xl">
            {/* Four coasts on a phone, all of them on a desktop. Eight names
                wrapped over four lines pushed the headline off the first
                screen, which is the one thing the hero has to do. */}
            <p className="li-mono text-[11px] uppercase tracking-[0.18em] text-[#F2EFE9]/85">
              <span className="sm:hidden">
                {realRegions.slice(0, 4).map((r) => r.label).join(" · ")}
              </span>
              <span className="hidden sm:inline">
                {realRegions.map((r) => regionName(r.key, r.label, t)).join(" · ")}
              </span>
            </p>
            <h1 className="li-serif mt-5 text-[3.25rem] font-normal leading-[0.98] text-[#F2EFE9] md:text-[4.75rem]">
              {/* Was "Every beach club." Rimini returns 7 results, so it was a
                  claim the data does not carry. The real count is both true and
                  the stronger line: it is the number our nearest competitor
                  cannot print. */}
              {t("hero_title_1", { n: String(total) })}
              <br />
              {t("hero_title_2")}
            </h1>
            <p className="mt-6 max-w-[44ch] text-lg leading-relaxed text-[#F2EFE9]/85">
              {t("hero_sub")}
            </p>
            <div className="mt-9">
              <SearchCard />
            </div>
          </div>
        </div>
      </section>

      {/* 2. Trust strip: every number here is computed from the database, so it
          can never drift away from what the site can actually show. */}
      <section className="border-b border-[#1C2430]/10 bg-[#E7E2D6]">
        <div className="mx-auto grid max-w-6xl gap-6 px-5 py-8 sm:grid-cols-3">
          {[
            { n: String(total), l: t("trust_clubs") },
            { n: String(realRegions.length), l: t("trust_coasts") },
            { n: "0", l: t("trust_free"), prefix: "€" },
          ].map((s) => (
            <div key={s.l} className="flex items-baseline gap-3">
              <span className="li-display text-3xl font-black tracking-tighter text-[#2C4FD6] md:text-4xl">
                {s.prefix}
                {s.n}
              </span>
              <span className="li-mono text-[11px] uppercase leading-tight tracking-widest text-[#5B6472]">
                {s.l}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* 3. Destinations: only coasts with real depth, each showing its true count. */}
      <section className="mx-auto mt-16 max-w-6xl px-5 md:mt-24">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h2 className="li-serif max-w-[16ch] text-3xl font-normal  text-[#1C2430] md:text-5xl">
            {t("destinations_title")}
          </h2>
          <p className="max-w-[36ch] text-sm leading-relaxed text-[#5B6472]">
            {t("destinations_sub")}
          </p>
        </div>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {realRegions.map((r) => (
            <Link
              key={r.key}
              to="/region/$key"
              params={{ key: r.key }}
              className="group relative block h-[300px] overflow-hidden rounded-2xl bg-[#1C2430] md:h-[340px]"
            >
              {r.image ? (
                <DeferredCover
                  src={r.image}
                  srcSet={r.imageSrcSet}
                  sizes={r.imageSrcSet ? CARD_SIZES : undefined}
                  width={r.imageWidth}
                  height={r.imageHeight}
                  alt={r.imageAlt ?? r.label}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                />
              ) : (
                <div className="h-full w-full bg-[#1C2430]" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1420]/85 via-[#0B1420]/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5">
                <p className="li-mono text-[11px] uppercase tracking-widest text-[#F2EFE9]/80">
                  {r.count} {t("stats_clubs")}
                </p>
                <h3 className="li-display mt-1 flex items-center gap-2 text-2xl font-black tracking-tighter text-[#F2EFE9]">
                  {regionName(r.key, r.label, t)}
                  <span className="li-mono text-sm text-[#F2EFE9] transition-transform duration-200 group-hover:translate-x-1">
                    →
                  </span>
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 4. Clubs grouped by coast — scannable the way a traveller actually
          decides: pick the coast first, then the club. */}
      <section className="mx-auto mt-16 max-w-6xl px-5 md:mt-24">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h2 className="li-serif text-3xl font-normal  text-[#1C2430] md:text-5xl">
            {t("region_clubs_title")}
          </h2>
          <Link
            to="/clubs"
            className="li-mono text-[12px] text-[#1C2430] underline decoration-[#2C4FD6] underline-offset-4 hover:text-[#2C4FD6]"
          >
            {t("all_clubs")}
          </Link>
        </div>
        <p className="mt-2 max-w-[52ch] text-sm leading-relaxed text-[#5B6472]">{t("live_sub")}</p>
        <div className="mt-10 flex flex-col gap-12">
          {clubsByRegion.map((r) => (
            <div key={r.key}>
              <div className="flex items-baseline justify-between gap-4 border-b border-[#1C2430]/15 pb-3">
                <h3 className="li-mono text-[12px] font-bold uppercase tracking-[0.18em] text-[#2C4FD6]">
                  {regionName(r.key, r.label, t)}
                </h3>
                <Link
                  to="/region/$key"
                  params={{ key: r.key }}
                  className="li-mono shrink-0 text-[11px] text-[#5B6472] underline decoration-[#2C4FD6] underline-offset-4 hover:text-[#1C2430]"
                >
                  {t("explore_region")} ({r.count})
                </Link>
              </div>
              <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {r.clubs.map((club) => (
                  <MiniClubCard key={club.slug} club={club} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 5. Why sunbedly — the three things that are true here and not on the
          platforms that sit between the traveller and the club. */}
      <section className="mx-auto mt-16 max-w-6xl px-5 md:mt-24">
        <h2 className="li-serif text-3xl font-normal  text-[#1C2430] md:text-5xl">
          {t("why_title")}
        </h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {[
            { tKey: "why_1_t", dKey: "why_1_d" },
            { tKey: "why_2_t", dKey: "why_2_d" },
            { tKey: "why_3_t", dKey: "why_3_d" },
          ].map((item) => (
            <div
              key={item.tKey}
              className="rounded-2xl border border-[#1C2430]/10 bg-white/60 p-6"
            >
              <h3 className="li-display text-xl font-bold tracking-tight text-[#1C2430]">
                {t(item.tKey)}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-[#5B6472]">{t(item.dKey)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 4. How it works */}
      <section className="mx-auto mt-16 max-w-6xl px-5 md:mt-24">
        <h2 className="li-serif text-3xl font-normal  text-[#1C2430] md:text-5xl">
          {t("how_title")}
        </h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {[
            { n: "01", tKey: "how_1_t", dKey: "how_1_d" },
            { n: "02", tKey: "how_2_t", dKey: "how_2_d" },
            { n: "03", tKey: "how_3_t", dKey: "how_3_d" },
          ].map((step) => (
            <div
              key={step.n}
              className="rounded-xl border border-[#1C2430]/10 bg-white/50 p-6"
            >
              <span className="li-mono text-3xl text-[#2C4FD6] md:text-4xl">{step.n}</span>
              <h3 className="li-display mt-3 text-xl font-bold tracking-tight text-[#1C2430] md:text-2xl">
                {t(step.tKey)}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-[#5B6472] md:text-base">
                {t(step.dKey)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* 5. Owners banner */}
      <section className="mt-16 bg-[#2C4FD6] md:mt-24">
        <div className="mx-auto max-w-6xl px-5 py-20 text-center md:py-28">
          <p className="li-mono text-[11px] uppercase tracking-widest text-[#F2EFE9]/85">
            {t("owners_eyebrow")}
          </p>
          <h2 className="li-serif mx-auto mt-3 max-w-[14ch] text-4xl font-normal leading-none  text-[#F2EFE9] md:text-7xl">
            {t("owners_title")}
          </h2>
          <p className="mx-auto mt-4 max-w-[38ch] text-base leading-relaxed text-[#F2EFE9]/85">
            {t("owners_sub")}
          </p>
          <Link
            to="/partner"
            className="li-mono group mt-8 inline-block text-[13px] text-[#F2EFE9]"
          >
            <span className="border-b border-[#F2EFE9]/50 pb-1 transition-colors group-hover:border-[#F2EFE9]">
              {t("owners_cta")} →
            </span>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
